/**
 * Stage 0 — Upload Documents
 *
 * Pre-analysis screen. New matters land here; the drafter uploads
 * the IDF + any supplementary material before Stage 1's scoring +
 * Q&A pipeline runs. The Stage 0 tab stays accessible from any
 * later stage so supplementary documents can be added after Stage 1
 * has already been scored / frozen.
 *
 * Composition:
 *   <UploadZone />            — dropzone + vision toggle + progress
 *   <UploadedFilesList />     — full editable file rows (delete / retry)
 *   <ExtractedFiguresStatus /> — figures count summary
 *   <Continue or Frozen note> — depending on matter.current_stage
 *                                / stage_1.is_frozen
 *
 * Refresh wiring:
 *   UploadZone fires onUploaded after each successful file POST.
 *   We bump filesRefreshKey + figuresRefreshKey from there so the
 *   list and the figures status refetch immediately rather than
 *   waiting for their internal poll cadence. We also call
 *   refreshMatter so the matter blob (idf_documents, completeness
 *   scores) stays current — matters re-entering Stage 0 from a
 *   frozen Stage 1 still benefit from a current matter snapshot.
 */
import React, { useEffect, useState } from 'react';
import { ArrowRight, Loader2, AlertTriangle } from 'lucide-react';

import { useAppStore } from '@/store';
import * as api from '@/api/client';
import UploadZone from '@/components/upload/UploadZone';
import UploadedFilesList from '@/components/upload/UploadedFilesList';
import ExtractedFiguresStatus from '@/components/upload/ExtractedFiguresStatus';


export default function Stage0() {
  const { matterState, setMatterState, setActiveStage } = useAppStore();
  const matter = matterState?.matter;
  const matterId = matter?.id || '';

  // Stage 1's is_frozen flag drives:
  //   - The "Rescore to incorporate" reminder under the upload zone
  //   - Backend refuses delete / retry while frozen, so we hide
  //     those affordances in UploadedFilesList too
  const stage1Frozen = !!matterState?.stages?.stage_1?.is_frozen;

  // Refresh tokens — bumped after every successful file upload so
  // UploadedFilesList + ExtractedFiguresStatus refetch out of band.
  // Each is a separate counter so a future caller can refetch one
  // without the other; today they always move together.
  const [filesRefreshKey, setFilesRefreshKey] = useState(0);
  const [figuresRefreshKey, setFiguresRefreshKey] = useState(0);

  // (document_id → job_id) map for async-path uploads. Populated by
  // UploadZone's onJobIdMapped callback when a POST /idf returns
  // 202; UploadedFilesList reads it to drive per-row step-progress
  // polling against /api/jobs/{job_id}/status. In-memory only —
  // a page reload mid-upload loses the mapping; the row still
  // shows a generic "Processing…" chip via its status alone.
  const [jobIdsByDocument, setJobIdsByDocument] = useState<
    Record<string, string>
  >({});

  // Track the live document list so we can pass docCount to
  // ExtractedFiguresStatus (its useEffect re-runs on docCount
  // change, picking up newly-extracted figures from a fresh
  // upload). The list owns the polling itself; we just observe.
  const [docCount, setDocCount] = useState<number>(0);
  const [indexedCount, setIndexedCount] = useState<number>(0);

  // Build a name → legacy idf_document map so UploadedFilesList can
  // surface char count + per-file warnings on primary IDF rows.
  // Recomputed whenever the matter blob changes; cheap.
  const legacyDocsByName = React.useMemo(() => {
    const m = new Map<string, { extraction_warnings?: string[]; extracted_text?: string }>();
    for (const d of (matterState?.idf_documents || []) as any[]) {
      m.set(d.filename, {
        extraction_warnings: d.extraction_warnings,
        extracted_text: d.extracted_text,
      });
    }
    return m;
  }, [matterState?.idf_documents]);

  const refreshMatter = React.useCallback(async () => {
    if (!matterId) return;
    try {
      const fresh = await api.getMatter(matterId);
      setMatterState(fresh);
    } catch { /* best-effort */ }
  }, [matterId, setMatterState]);

  const handleUploaded = React.useCallback(() => {
    setFilesRefreshKey((k) => k + 1);
    setFiguresRefreshKey((k) => k + 1);
    refreshMatter().catch(() => {});
  }, [refreshMatter]);

  // ───── Continue / advance flow ────────────────────────────────
  // Only visible when current_stage is still stage_0. Re-entries
  // from stage_1+ replace this with the frozen-stage-1 reminder
  // (or nothing, if stage_1 isn't frozen).
  const onStage0 = matter?.current_stage === 'stage_0';
  const [advancing, setAdvancing] = useState(false);
  const [advanceError, setAdvanceError] = useState<string | null>(null);

  const canAdvance = onStage0 && indexedCount > 0 && !advancing;
  const disabledReason = onStage0 && indexedCount === 0
    ? 'Upload and process at least one document first'
    : '';

  const handleAdvance = async () => {
    if (!matterId || advancing || !onStage0) return;
    setAdvancing(true);
    setAdvanceError(null);
    try {
      await api.advanceStage0(matterId);
      // Re-fetch BEFORE flipping activeStage so TopBar's currentIdx
      // (driven by matter.current_stage) reflects the promotion;
      // otherwise Stage 1 would render but its tab would still
      // look locked.
      const fresh = await api.getMatter(matterId);
      setMatterState(fresh);
      setActiveStage('stage_1');
    } catch (e: any) {
      setAdvanceError(e?.response?.data?.detail || 'Could not advance to Stage 1.');
    } finally {
      setAdvancing(false);
    }
  };

  if (!matter) return null;

  return (
    <div
      data-testid="stage-0"
      className="max-w-4xl mx-auto pt-16 pb-12 px-6"
    >
      <div className="mb-6">
        <div className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-1">
          Stage 0
        </div>
        <h1
          className="text-2xl font-semibold tracking-tight text-zinc-900"
          style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}
        >
          Upload Documents
        </h1>
        <p className="text-sm text-zinc-600 mt-1 leading-relaxed max-w-2xl">
          Upload your IDF and any supporting material before analysis
          begins. Stage 1 picks up the moment one document finishes
          indexing — you can keep adding supplementary material from
          this tab even after analysis has started.
        </p>
      </div>

      {/* Stage-1-frozen reminder. Only meaningful for re-entries
          (current_stage > stage_0 AND stage_1 is frozen). Tells the
          drafter that newly-uploaded docs won't be picked up by the
          locked-in scoring until they Rescore. */}
      {!onStage0 && stage1Frozen && (
        <div
          data-testid="stage-0-frozen-note"
          className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-sm text-sm text-amber-900 flex items-start gap-2"
        >
          <AlertTriangle size={16} className="mt-0.5 flex-shrink-0 text-amber-600" />
          <span>
            Stage 1 is frozen. New documents will not be included in
            the analysis until you click Rescore in Stage 1.
          </span>
        </div>
      )}

      <div className="space-y-6">
        <UploadZone
          matterId={matterId}
          onUploaded={handleUploaded}
          onJobIdMapped={(documentId, jobId) =>
            setJobIdsByDocument((prev) => ({ ...prev, [documentId]: jobId }))
          }
        />

        <UploadedFilesList
          matterId={matterId}
          mode="editable"
          refreshKey={filesRefreshKey}
          isFrozen={stage1Frozen}
          legacyDocsByName={legacyDocsByName}
          jobIdsByDocument={jobIdsByDocument}
          onDocumentsChange={(docs) => {
            setDocCount(docs.length);
            setIndexedCount(docs.filter((d) => d.status === 'indexed').length);
          }}
        />

        {/*
          ExtractedFiguresStatus hides itself when docCount is 0 or
          when no figures have been extracted yet, so it's safe to
          mount unconditionally. refreshKey forces a refetch after
          each successful upload even when docCount hasn't shifted
          (e.g. a re-upload of an existing filename).
        */}
        <ExtractedFiguresStatus
          matterId={matterId}
          docCount={docCount}
          refreshKey={figuresRefreshKey}
        />
      </div>

      {advanceError && (
        <div
          data-testid="stage-0-error"
          className="mt-4 p-3 bg-red-50 border border-red-200 rounded-sm text-sm text-red-800 flex items-center gap-2"
        >
          <AlertTriangle size={14} /> {advanceError}
        </div>
      )}

      {onStage0 && (
        <div className="mt-6 flex justify-end">
          <button
            type="button"
            data-testid="stage-0-continue-button"
            onClick={handleAdvance}
            disabled={!canAdvance}
            title={disabledReason || undefined}
            className={
              'inline-flex items-center gap-2 px-4 py-2 rounded-sm text-sm font-medium transition-colors ' +
              (canAdvance
                ? 'bg-[#002FA7] text-white hover:bg-[#002277]'
                : 'bg-zinc-200 text-zinc-500 cursor-not-allowed')
            }
          >
            {advancing
              ? <Loader2 size={14} className="animate-spin" />
              : <ArrowRight size={14} />}
            Continue to Analysis
          </button>
        </div>
      )}
    </div>
  );
}
